<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">
      <AssetsDashboard />

      <div class="assets-grid-card">
        <div class="assets-grid-card__header">
          <div class="assets-grid-title">
            <div class="assets-grid-title__icon">
              <i class="bi bi-hdd-rack-fill" />
            </div>
            <div class="assets-grid-title__text">
              {{ $t('components.assets.dataGrids.assetsList') }}
            </div>
          </div>

          <div class="assets-grid-controls">
            <div class="assets-search">
              <i class="bi bi-search" />
              <input
                v-model="searchText"
                type="search"
                :placeholder="$t('components.assets.dataGrids.searchPlaceholder')"
                @input="onSearchInput"
                @keyup.esc="clearSearch"
              >
              <button
                v-if="searchText"
                type="button"
                class="assets-search__clear"
                :title="$t('components.assets.dataGrids.clearSearch')"
                @click="clearSearch"
              >
                <i class="bi bi-x-lg" />
              </button>
            </div>

            <button
              type="button"
              class="assets-toolbar-btn"
              :class="{ active: showFilters }"
              :title="$t('components.assets.dataGrids.filters')"
              @click="toggleFilters"
            >
              <i class="bi bi-funnel" />
              <span>{{ $t('components.assets.dataGrids.filters') }}</span>
            </button>

            <button
              v-if="hasFilters || searchText"
              type="button"
              class="assets-toolbar-btn"
              :title="$t('components.assets.dataGrids.clear')"
              @click="clearAll"
            >
              <i class="bi bi-eraser" />
              <span>{{ $t('components.assets.dataGrids.clear') }}</span>
            </button>

            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('components.assets.dataGrids.refresh')"
              @click="refreshGrid"
            >
              <i class="bi bi-arrow-clockwise" />
              <span>{{ $t('components.assets.dataGrids.refresh') }}</span>
            </button>

            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('components.assets.dataGrids.columns')"
              @click="openColumnChooser"
            >
              <i class="bi bi-layout-three-columns" />
              <span>{{ $t('components.assets.dataGrids.columns') }}</span>
            </button>
          </div>
        </div>

        <div class="assets-grid-card__body">
          <DxDataGrid
            :ref="gridRef"
            :data-source="store"
            :hover-state-enabled="true"
            :remote-operations="true"
            :column-auto-width="false"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :scrolling="scrollingConfig"
            :column-hiding-enabled="true"
            :word-wrap-enabled="true"
            @optionChanged="onGridOptionChanged"
            @contentReady="onGridContentReady"
            @contextMenuPreparing="onContextMenuPreparing"
            @toolbarPreparing="onToolbarPreparing"
            @cellPrepared="onCellPrepared"
          >
            <DxHeaderFilter :visible="showFilters" />
            <DxFilterRow :visible="showFilters && !isMobile" />
            <DxPaging :page-size="20" />
            <DxPager
              :allowed-page-sizes="[5, 10, 20, 25, 50]"
              :show-info="true"
              :show-navigation-buttons="true"
              :show-page-size-selector="true"
              :visible="true"
            />
            <DxColumnChooser
              :enabled="true"
              mode="select"
              :allow-search="true"
              :title="$t('components.assets.dataGrids.columns')"
            />

            <DxColumn
              name="deviceData"
              data-field="name"
              :caption="$t('components.assets.dataGrids.deviceData')"
              cell-template="device-data-template"
              :allow-hiding="false"
              :show-in-column-chooser="false"
            />

            <DxColumn
              name="groupNames"
              data-field="group_names"
              :caption="$t('components.assets.dataGrids.groups')"
              cell-template="group-names-template"
              :width="250"
              alignment="center"
              :visible="!isMobile"
              :hiding-priority="1"
              :allow-hiding="true"
              :allow-filtering="false"
            />

            <DxColumn
              name="sensors"
              :caption="$t('components.assets.dataGrids.sensors')"
              cell-template="sensors-template"
              :allow-filtering="false"
              alignment="center"
              :width="170"
              :hiding-priority="2"
              :allow-hiding="false"
              :show-in-column-chooser="false"
            />

            <DxColumn
              name="batteryStatus"
              :caption="$t('components.assets.dataGrids.batteryStatus')"
              cell-template="battery-template"
              :allow-filtering="false"
              alignment="center"
              :width="150"
              :hiding-priority="3"
              :allow-hiding="false"
              :show-in-column-chooser="false"
            />

            <DxColumn
              name="xAxis"
              :caption="$t('components.assets.dataGrids.xAxis')"
              cell-template="x-axis-template"
              :allow-filtering="false"
              alignment="center"
              :width="isMobile ? 110 : 150"
              :hiding-priority="7"
              :visible="false"
            />

            <DxColumn
              name="yAxis"
              :caption="$t('components.assets.dataGrids.yAxis')"
              cell-template="y-axis-template"
              :allow-filtering="false"
              alignment="center"
              :width="isMobile ? 110 : 150"
              :hiding-priority="8"
              :visible="false"
            />

            <DxColumn
              name="zAxis"
              :caption="$t('components.assets.dataGrids.zAxis')"
              cell-template="z-axis-template"
              :allow-filtering="false"
              alignment="center"
              :width="isMobile ? 110 : 150"
              :hiding-priority="9"
              :visible="false"
            />

            <DxColumn
              name="magnitude"
              :caption="$t('components.assets.dataGrids.magnitude')"
              cell-template="magnitude-template"
              :allow-filtering="false"
              alignment="center"
              :width="isMobile ? 140 : 190"
              :hiding-priority="10"
              :visible="false"
            />

            <DxColumn
              name="magnitudeNoGravity"
              :caption="$t('components.assets.dataGrids.magnitudeNoGravity')"
              cell-template="magnitude-no-gravity-template"
              :allow-filtering="false"
              alignment="center"
              :width="isMobile ? 140 : 210"
              :hiding-priority="11"
              :visible="false"
            />

            <DxColumn
              name="lastActivity"
              :caption="$t('components.assets.dataGrids.lastActivity')"
              cell-template="last-activity-template"
              :allow-filtering="false"
              alignment="center"
              :width="250"
              :hiding-priority="4"
              :allow-hiding="false"
              :show-in-column-chooser="false"
            />

            <DxColumn
              name="actions"
              :caption="$t('components.assets.dataGrids.actions')"
              :width="isMobile ? 90 : 120"
              :allow-filtering="false"
              alignment="center"
              cell-template="actions-template"
              :hiding-priority="5"
              :allow-hiding="false"
              :show-in-column-chooser="false"
            />

            <template #device-data-template="{ data }">
              <div class="device-data-cell">
                <div class="device-name-badge">
                  <div class="device-icon">
                    <i class="bi bi-hdd-rack-fill" />
                  </div>
                  <div class="device-content">
                    <div class="device-title">
                      {{ data.data.name || '—' }}
                    </div>
                    <div
                      class="device-meta"
                      v-if="getSerialNumber(data.data) !== '—'"
                    >
                      <i class="bi bi-upc-scan" />
                      <span>{{ getSerialNumber(data.data) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <template #group-names-template="{ data }">
              <div class="groups-cell">
                <div
                  v-if="getGroupNames(data.data).length"
                  class="groups-list"
                >
                  <div
                    v-for="(groupName, index) in getGroupNames(data.data)"
                    :key="`group-${data.data.id}-${index}`"
                    class="param-chip group-chip"
                  >
                    <i class="bi bi-diagram-3" />
                    <span>{{ groupName }}</span>
                  </div>
                </div>
                <div
                  v-else
                  class="param-chip param-chip--empty"
                >
                  —
                </div>
              </div>
            </template>

            <template #sensors-template="{ data }">
              <div class="sensors-cell">
                <template v-if="getTemperature(data.data) !== '—' || getHumidity(data.data) !== '—' || getPowerSupply(data.data) !== '—' || getGatewayPower(data.data) !== '—'">
                  <div
                    v-if="getTemperature(data.data) !== '—'"
                    class="param-chip sensor-chip"
                  >
                    <i class="bi bi-thermometer-half" />
                    <span class="sensor-chip__label">{{ $t('components.assets.dataGrids.temperature') }}</span>
                    <span class="sensor-chip__value">{{ getTemperature(data.data) }}</span>
                  </div>
                  <div
                    v-if="getHumidity(data.data) !== '—'"
                    class="param-chip sensor-chip"
                  >
                    <i class="bi bi-droplet-half" />
                    <span class="sensor-chip__label">{{ $t('components.assets.dataGrids.humidity') }}</span>
                    <span class="sensor-chip__value">{{ getHumidity(data.data) }}</span>
                  </div>
                  <div
                    v-if="getPowerSupply(data.data) !== '—'"
                    class="param-chip sensor-chip"
                  >
                    <i class="bi bi-lightning-charge-fill" />
                    <span class="sensor-chip__label">{{ $t('components.assets.dataGrids.power') }}</span>
                    <span class="sensor-chip__value">{{ getPowerSupply(data.data) }}</span>
                  </div>
                  <div
                    v-if="getGatewayPower(data.data) !== '—'"
                    class="param-chip sensor-chip"
                  >
                    <i class="bi bi-broadcast" />
                    <span class="sensor-chip__label">{{ $t('components.assets.dataGrids.gatewayPower') }}</span>
                    <span class="sensor-chip__value">{{ getGatewayPower(data.data) }}</span>
                  </div>
                </template>
                <div
                  v-else
                  class="param-chip param-chip--empty"
                >
                  —
                </div>
              </div>
            </template>

            <template #battery-template="{ data }">
              <div class="single-param-cell">
                <div
                  v-if="getBatteryStatus(data.data) !== '—'"
                  class="param-chip battery-chip"
                  :class="getBatteryStatusClass(data.data)"
                >
                  <i class="bi bi-battery-charging" />
                  <span>{{ getBatteryStatus(data.data) }}</span>
                </div>
                <div
                  v-else-if="getGpsPowerStatusText(data.data) !== '—'"
                  class="param-chip battery-chip"
                  :class="getGpsPowerStatusClass(data.data)"
                >
                  <i class="bi bi-plug-fill" />
                  <span>{{ getGpsPowerStatusText(data.data) }}</span>
                </div>
                <div
                  v-else
                  class="param-chip param-chip--empty"
                >
                  —
                </div>
              </div>
            </template>

            <template #x-axis-template="{ data }">
              <div class="single-param-cell">
                <div
                  class="param-chip"
                  v-if="getAxisValue(data.data, 'X_AXIS') !== '—'"
                >
                  <i class="bi bi-arrows-move" />
                  <span>{{ getAxisValue(data.data, 'X_AXIS') }}</span>
                </div>
                <div
                  class="param-chip param-chip--empty"
                  v-else
                >
                  —
                </div>
              </div>
            </template>

            <template #y-axis-template="{ data }">
              <div class="single-param-cell">
                <div
                  class="param-chip"
                  v-if="getAxisValue(data.data, 'Y_AXIS') !== '—'"
                >
                  <i class="bi bi-arrows-vertical" />
                  <span>{{ getAxisValue(data.data, 'Y_AXIS') }}</span>
                </div>
                <div
                  class="param-chip param-chip--empty"
                  v-else
                >
                  —
                </div>
              </div>
            </template>

            <template #z-axis-template="{ data }">
              <div class="single-param-cell">
                <div
                  class="param-chip"
                  v-if="getAxisValue(data.data, 'Z_AXIS') !== '—'"
                >
                  <i class="bi bi-arrows-collapse" />
                  <span>{{ getAxisValue(data.data, 'Z_AXIS') }}</span>
                </div>
                <div
                  class="param-chip param-chip--empty"
                  v-else
                >
                  —
                </div>
              </div>
            </template>

            <template #magnitude-template="{ data }">
              <div class="single-param-cell">
                <div
                  class="param-chip"
                  v-if="getAxisMagnitude(data.data) !== '—'"
                >
                  <i class="bi bi-activity" />
                  <span>{{ getAxisMagnitude(data.data) }}</span>
                </div>
                <div
                  class="param-chip param-chip--empty"
                  v-else
                >
                  —
                </div>
              </div>
            </template>

            <template #magnitude-no-gravity-template="{ data }">
              <div class="single-param-cell">
                <div
                  class="param-chip"
                  v-if="getAxisMagnitudeNoGravity(data.data) !== '—'"
                >
                  <i class="bi bi-activity" />
                  <span>{{ getAxisMagnitudeNoGravity(data.data) }}</span>
                </div>
                <div
                  class="param-chip param-chip--empty"
                  v-else
                >
                  —
                </div>
              </div>
            </template>

            <template #last-activity-template="{ data }">
              <div class="last-activity-wrapper">
                <div
                  class="status-pill"
                  :class="getLastActivityClass(data.data)"
                  :title="`${getOnlineStatusLabel(data.data)} • ${getLastActivity(data.data)}`"
                >
                  <div class="status-pill__top">
                    <span
                      class="status-dot"
                      aria-hidden="true"
                    />
                    <span class="status-label">{{ getOnlineStatusLabel(data.data) }}</span>
                  </div>
                  <span class="status-time">{{ getLastActivity(data.data) }}</span>
                </div>
              </div>
            </template>

            <template #actions-template="{ data }">
              <div class="action-buttons">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :title="$t('components.assets.dataGrids.details')"
                  @click="onPreview({ row: { key: data.data.id, data: data.data } })"
                >
                  <i class="bi bi-eye" />
                </button>
                <button
                  v-if="shouldShowLocationButton(data.data)"
                  type="button"
                  class="btn btn-sm btn-outline-success"
                  :title="$t('components.assets.dataGrids.setDefaultLocation')"
                  @click="openDefaultLocationModal(data.data)"
                >
                  <i class="bi bi-geo-alt" />
                </button>
                <button
                  v-if="canEdit && !isMobile"
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  :title="$t('components.assets.images.manage')"
                  @click="openAssetImagesModal(data.data)"
                >
                  <i class="bi bi-pencil" />
                </button>
              </div>
            </template>
          </DxDataGrid>
        </div>
      </div>
    </div>

    <!-- Asset Default Location Modal -->
    <AssetDefaultLocationModal
      ref="defaultLocationModalRef"
      @saved="handleAssetLocationSaved"
    />
    <AssetImagesModal
      ref="assetImagesModalRef"
      @saved="handleAssetImagesSaved"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, watch, ref } from 'vue'
import AssetsDashboard from '@/components/content/AssetsDashboard.vue'
import AssetDefaultLocationModal from '@/components/content/_modals/AssetDefaultLocationModal.vue'
import AssetImagesModal from '@/components/content/_modals/AssetImagesModal.vue'
import { useI18n } from 'vue-i18n'
import {
  DxDataGrid,
  DxColumn,
  DxColumnChooser,
  DxFilterRow,
  DxHeaderFilter,
  DxPaging,
  DxPager,
} from 'devextreme-vue/data-grid'
import CustomStore from 'devextreme/data/custom_store'
import { getAssetsGrid } from '@/crud/asset/getters'
import { getHumanDateTime, canEditAssetGroups } from '@/utils/tools'
import { computeAxisMagnitude, computeAxisMagnitudeNoGravity } from '@/utils/asset-history-series'

function getStore() {
  return new CustomStore({
    cacheRawData: false,
    key: 'id',
    load(loadOptions) {
      return new Promise((resolve, reject) => {
        const skip = loadOptions.skip || 0
        const take = loadOptions.take || 25
        let searchValue = typeof loadOptions.searchValue === 'string' ? loadOptions.searchValue.trim() : ''

        // Build search value from filters (fallback when search panel not used)
        if (!searchValue && loadOptions.filter) {
          const filter = loadOptions.filter

          // Helper function to extract filter value
          const extractFilterValue = (f: any): string => {
            if (Array.isArray(f) && f.length >= 3) {
              // Simple filter: ["field", "operator", "value"]
              if (typeof f[0] === 'string' && f[0] === 'name') {
                return f[2] || ''
              }
            }
            return ''
          }

          if (Array.isArray(filter)) {
            // Check if it's a simple filter or complex
            if (typeof filter[0] === 'string') {
              // Simple filter: ["name", "contains", "value"]
              searchValue = extractFilterValue(filter)
            } else {
              // Complex filter - find first valid filter
              for (const f of filter) {
                const value = extractFilterValue(f)
                if (value) {
                  searchValue = value
                  break
                }
              }
            }
          }
        }

        getAssetsGrid(skip, take, searchValue)
          .then((response: any) => {
            const data = response.data.data || []
            const totalCount = response.data.totalCount || 0

            resolve({
              data: data,
              totalCount: totalCount
            })
          })
          .catch((error) => {
            console.error('Assets API load error:', error)
            reject(error)
          })
      })
    }
  })
}

const ASSETS_GRID_STATE_KEY = 'assetsGridState'
const ASSETS_GRID_CUSTOM_COLUMNS_KEY = 'assetsGridCustomColumns'
const CUSTOM_COLUMN_NAMES = new Set([
  'xAxis',
  'yAxis',
  'zAxis',
  'magnitude',
  'magnitudeNoGravity'
])

function filterCustomColumnNames(names: unknown): string[] {
  if (!Array.isArray(names)) return []
  return names.filter((name) => typeof name === 'string' && CUSTOM_COLUMN_NAMES.has(name))
}

export default defineComponent({
  name: 'AssetView',
  components: {
    AssetsDashboard,
    AssetDefaultLocationModal,
    AssetImagesModal,
    DxDataGrid,
    DxColumn,
    DxColumnChooser,
    DxFilterRow,
    DxHeaderFilter,
    DxPaging,
    DxPager,
  },
  setup(props, context) {
    const i18n = useI18n()
    context.emit('pageName', i18n.t('aside.menu.pages.assets'))

    watch(() => i18n.locale.value, () => {
      context.emit('pageName', i18n.t('aside.menu.pages.assets'))
    })
  },
  data(): {
    store: any,
    gridRef: string,
    hasFilters: boolean,
    windowWidth: number,
    showFilters: boolean,
    searchText: string,
    searchDebounceTimeout: any,
    hasAppliedCustomColumns: boolean,
    isApplyingCustomColumns: boolean,
  } {
    return {
      store: getStore(),
      gridRef: 'assetsGrid',
      hasFilters: false,
      windowWidth: window.innerWidth,
      showFilters: false,
      searchText: '',
      searchDebounceTimeout: null,
      hasAppliedCustomColumns: false,
      isApplyingCustomColumns: false,
    }
  },
  computed: {
    grid() {
      // @ts-ignore
      return this.$refs[this.gridRef]?.instance
    },
    isMobile() {
      return this.windowWidth <= 767
    },
    canEdit() {
      return canEditAssetGroups()
    },
    scrollingConfig() {
      return {
        mode: 'standard',
        rowRenderingMode: 'standard',
        columnRenderingMode: 'virtual',
        useNative: true,
        scrollByContent: true,
        scrollByThumb: true,
        showScrollbar: 'onScroll',
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout)
    }
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    onCellPrepared(e: any) {
      if (e.rowType === 'data') {
        e.cellElement.style.alignContent = 'center'
      }
    },
    detectFilters() {
      this.hasFilters = !!this.grid?.getCombinedFilter()
    },
    toggleFilters() {
      this.showFilters = !this.showFilters
    },
    onSearchInput() {
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout)
      }
      this.searchDebounceTimeout = setTimeout(() => {
        this.grid?.searchByText(this.searchText || '')
      }, 250)
    },
    clearSearch() {
      this.searchText = ''
      this.grid?.searchByText('')
    },
    clearAll() {
      this.clearSearch()
      this.grid?.clearFilter()
    },
    refreshGrid() {
      this.grid?.refresh()
    },
    openColumnChooser() {
      this.grid?.showColumnChooser()
    },
    onGridContentReady() {
      this.applyCustomColumnVisibility()
    },
    loadCustomColumnVisibility(): string[] {
      if (typeof window === 'undefined') return []
      const raw = window.localStorage.getItem(ASSETS_GRID_CUSTOM_COLUMNS_KEY)
      if (raw) {
        try {
          const parsed = JSON.parse(raw)
          return filterCustomColumnNames(parsed?.visible)
        } catch (error) {
          console.warn('Failed to load custom column visibility', error)
        }
      }

      const legacyRaw = window.localStorage.getItem(ASSETS_GRID_STATE_KEY)
      if (!legacyRaw) return []
      try {
        const legacyParsed = JSON.parse(legacyRaw)
        const legacyColumns = Array.isArray(legacyParsed?.columns) ? legacyParsed.columns : []
        const visibleLegacy = legacyColumns
          .filter((column: any) => column?.visible !== false)
          .map((column: any) => column?.name || column?.dataField)
        return filterCustomColumnNames(visibleLegacy)
      } catch (error) {
        console.warn('Failed to migrate legacy column visibility', error)
        return []
      }
    },
    persistCustomColumnVisibility() {
      if (typeof window === 'undefined' || !this.grid) return
      const visible = Array.from(CUSTOM_COLUMN_NAMES)
        .filter((name) => this.grid?.columnOption(name, 'visible'))
      const payload = { visible }
      window.localStorage.setItem(ASSETS_GRID_CUSTOM_COLUMNS_KEY, JSON.stringify(payload))
    },
    applyCustomColumnVisibility() {
      if (this.hasAppliedCustomColumns || !this.grid) return
      const visible = this.loadCustomColumnVisibility()
      this.isApplyingCustomColumns = true
      CUSTOM_COLUMN_NAMES.forEach((name) => {
        const shouldBeVisible = visible.includes(name)
        this.grid?.columnOption(name, 'visible', shouldBeVisible)
      })
      this.isApplyingCustomColumns = false
      this.hasAppliedCustomColumns = true
    },
    onGridOptionChanged(e: any) {
      this.detectFilters()
      if (this.isApplyingCustomColumns) return
      const fullName = typeof e?.fullName === 'string' ? e.fullName : ''
      const match = fullName.match(/^columns\[(\d+)\]\.visible$/)
      if (!match) return
      const index = Number(match[1])
      if (!Number.isFinite(index)) return
      const column = e?.component?.columnOption(index)
      const columnName = column?.name || column?.dataField
      if (!CUSTOM_COLUMN_NAMES.has(columnName)) return
      this.persistCustomColumnVisibility()
    },
    onContextMenuPreparing(e: any) {
      if (e?.target !== 'header' || !Array.isArray(e.items)) return
      e.items = e.items.filter((item: any) => {
        const name = item?.name
        return typeof name !== 'string' || !name.toLowerCase().includes('columnchooser')
      })
    },
    onToolbarPreparing(e: any) {
      const items = e?.toolbarOptions?.items
      if (!Array.isArray(items)) return
      e.toolbarOptions.items = items.filter((item: any) => item?.name !== 'columnChooserButton')
      if (!e.toolbarOptions.items.length) {
        e.toolbarOptions.visible = false
      }
    },
    getLatestParamNumber(asset: any, paramName: string): number | null {
      if (!asset.latest_params || !asset.latest_params.length) return null
      const param = asset.latest_params.find((p: any) =>
        p.can_params_io && p.can_params_io.param_name === paramName
      )
      if (!param || param.value === null || param.value === undefined) return null
      const numeric = typeof param.value === 'number' ? param.value : parseFloat(param.value)
      return Number.isFinite(numeric) ? numeric : null
    },
    formatAxisValue(value: number): string {
      const formatted = Number.isInteger(value) ? value.toString() : value.toFixed(2)
      return `${formatted} mG`
    },
    getAxisValue(asset: any, paramName: string) {
      const value = this.getLatestParamNumber(asset, paramName)
      if (value === null) return '—'
      return this.formatAxisValue(value)
    },
    getAxisMagnitudeValue(asset: any): number | null {
      const xValue = this.getLatestParamNumber(asset, 'X_AXIS')
      const yValue = this.getLatestParamNumber(asset, 'Y_AXIS')
      const zValue = this.getLatestParamNumber(asset, 'Z_AXIS')

      if (xValue === null || yValue === null || zValue === null) return null

      return computeAxisMagnitude(xValue, yValue, zValue)
    },
    getAxisMagnitude(asset: any) {
      const magnitude = this.getAxisMagnitudeValue(asset)
      if (magnitude === null) return '—'
      return `${magnitude.toFixed(2)} mG`
    },
    getAxisMagnitudeNoGravity(asset: any) {
      const magnitude = this.getAxisMagnitudeValue(asset)
      if (magnitude === null) return '—'
      const noGravity = computeAxisMagnitudeNoGravity(magnitude)
      return `${noGravity.toFixed(2)} mG`
    },
    getSerialNumber(asset: any) {
      if (asset.latest_params && asset.latest_params.length) {
        const macParam = asset.latest_params.find((p: any) =>
          p.can_params_io && p.can_params_io.param_name === 'TL_EYE_MAC_ADDRESS'
        )
        if (macParam && macParam.value) {
          return macParam.value
        }
      }
      return asset.serial_number || '—'
    },
    getGroupNames(asset: any): string[] {
      const names: string[] = []
      if (Array.isArray(asset.group_names)) {
        asset.group_names.forEach((name: unknown) => {
          if (typeof name === 'string' && name.trim()) {
            names.push(name.trim())
          }
        })
      } else if (Array.isArray(asset.asset_groups)) {
        asset.asset_groups.forEach((group: any) => {
          const name = typeof group?.name === 'string' ? group.name.trim() : ''
          if (name) {
            names.push(name)
          }
        })
      }

      return Array.from(new Set(names))
    },
    isDknMpsAsset(asset: any): boolean {
      const modelId = Number(asset?.device_model_id ?? asset?.device_model?.id)
      if (modelId === 25 || modelId === 30) return true
      const params = asset?.latest_params
      if (!Array.isArray(params) || !params.length) return false
      return params.some((p: any) => {
        const name = p?.can_params_io?.param_name
        return typeof name === 'string' && name.startsWith('DKN_MPS_')
      })
    },
    getTemperature(asset: any) {
      if (!asset.latest_params || !asset.latest_params.length) return '—'
      const tempParam = asset.latest_params.find((p: any) =>
        p.can_params_io && p.can_params_io.param_name === 'TL_EYE_TEMPERATURE'
      )
      if (tempParam && tempParam.value !== null && tempParam.value !== undefined) {
        return parseFloat(tempParam.value).toFixed(2) + ' °C'
      }
      if (this.isDknMpsAsset(asset)) {
        const probe = this.getLatestParamNumber(asset, 'DKN_MPS_PROBE_1')
        if (probe !== null) return probe.toFixed(2) + ' °C'
      }
      return '—'
    },
    getPowerSupply(asset: any) {
      if (!asset.latest_params || !asset.latest_params.length) return '—'
      const powerParam = asset.latest_params.find((p: any) =>
        p.can_params_io && (
          p.can_params_io.param_name === 'TL_EYE_POWER_SUPPLY' ||
          p.can_params_io.param_name === 'TL_EYE_BATTERY_VOLTAGE'
        )
      )
      if (!powerParam || powerParam.value === null || powerParam.value === undefined) return '—'
      // Convert mV to V (e.g., 3201 -> 3.2V)
      const voltage = parseFloat(powerParam.value) / 1000
      return voltage.toFixed(1) + 'V'
    },
    getGatewayPower(asset: any) {
      const value = this.getLatestParamNumber(asset, 'DEV_POWER_SUPPLY_VOLTAGE')
      if (value === null) return '—'
      return (value / 1000).toFixed(2) + ' V'
    },
    getGpsPowerStatusText(asset: any) {
      const value = this.getLatestParamNumber(asset, 'DEV_POWER_SUPPLY_VOLTAGE')
      if (value === null) return '—'
      const volts = value / 1000
      if (volts === 0) return this.$t('components.assets.dataGrids.gpsPowerNone')
      if (volts < 23) return this.$t('components.assets.dataGrids.gpsPowerLow')
      return this.$t('common.ok')
    },
    getGpsPowerStatusClass(asset: any) {
      const value = this.getLatestParamNumber(asset, 'DEV_POWER_SUPPLY_VOLTAGE')
      if (value === null) return ''
      const volts = value / 1000
      if (volts === 0) return 'status-bad'
      if (volts < 23) return 'status-warn'
      return 'status-ok'
    },
    getHumidity(asset: any) {
      if (!asset.latest_params || !asset.latest_params.length) return '—'
      const humidityParam = asset.latest_params.find((p: any) =>
        p.can_params_io && p.can_params_io.param_name === 'TL_EYE_HUMIDITY'
      )
      if (!humidityParam || humidityParam.value === null || humidityParam.value === undefined) return '—'
      return parseFloat(humidityParam.value).toFixed(1) + '%'
    },
    getBatteryStatus(asset: any) {
      if (!asset.latest_params || !asset.latest_params.length) return '—'
      const lowBatteryParam = asset.latest_params.find((p: any) =>
        p.can_params_io && p.can_params_io.param_name === 'TL_EYE_LOW_BATTERY'
      )
      if (lowBatteryParam && lowBatteryParam.value !== null && lowBatteryParam.value !== undefined) {
        return parseInt(lowBatteryParam.value) === 0 ? this.$t('common.ok') : this.$t('common.bad')
      }
      if (this.isDknMpsAsset(asset)) {
        const onOff = this.getLatestParamNumber(asset, 'DKN_MPS_STATUS_ON_OFF')
        if (onOff !== null) return onOff === 1 ? this.$t('common.on') : this.$t('common.off')
      }
      return '—'
    },
    getBatteryStatusClass(asset: any) {
      if (!asset.latest_params || !asset.latest_params.length) return ''
      const lowBatteryParam = asset.latest_params.find((p: any) =>
        p.can_params_io && p.can_params_io.param_name === 'TL_EYE_LOW_BATTERY'
      )
      if (lowBatteryParam && lowBatteryParam.value !== null && lowBatteryParam.value !== undefined) {
        return parseInt(lowBatteryParam.value) === 0 ? 'status-ok' : 'status-bad'
      }
      if (this.isDknMpsAsset(asset)) {
        const onOff = this.getLatestParamNumber(asset, 'DKN_MPS_STATUS_ON_OFF')
        if (onOff !== null) return onOff === 1 ? 'status-ok' : 'status-bad'
      }
      return ''
    },
    getLastActivity(asset: any) {
      if (!asset.last_seen) return '—'
      return getHumanDateTime(asset.last_seen)
    },
    getOnlineStatusLabel(asset: any) {
      return this.getLastActivityClass(asset) === 'active'
        ? this.$t('common.online')
        : this.$t('common.offline')
    },
    getLastActivityClass(asset: any) {
      if (!asset.last_seen) return 'inactive'
      const lastSeen = asset.last_seen
      const now = Math.floor(Date.now() / 1000)
      const diff = now - lastSeen
      const twentyFourHours = 24 * 60 * 60

      return diff < twentyFourHours ? 'active' : 'inactive'
    },
    onPreview(e: any) {
      const assetId = e.row.key
      if (assetId) {
        const query = typeof assetId === 'string' ? { source: 'device' } : undefined
        this.$router.push({ name: 'AssetDetails', params: { id: assetId }, query })
      }
    },
    hasAnyParams(asset: any) {
      return this.getTemperature(asset) !== '—' ||
             this.getHumidity(asset) !== '—' ||
             this.getPowerSupply(asset) !== '—' ||
             this.getGatewayPower(asset) !== '—' ||
             this.getBatteryStatus(asset) !== '—'
    },
    shouldShowLocationButton(asset: any) {
      // Check if user has permission
      if (!this.canEdit) {
        return false;
      }

      // Check if asset has valid coordinates in latest_params
      const latestParams = asset.latest_params && asset.latest_params.length > 0 ? asset.latest_params[0] : null;

      if (!latestParams) {
        // No params data - show button
        return true;
      }

      const lat = latestParams.latitude;
      const lng = latestParams.longitude;

      // Show button if coordinates are missing, null, 0, or invalid
      if (!lat || !lng ||
          lat === null || lng === null ||
          parseFloat(lat) === 0 || parseFloat(lng) === 0 ||
          isNaN(parseFloat(lat)) || isNaN(parseFloat(lng))) {
        return true;
      }

      // Has valid coordinates - hide button
      return false;
    },
    openDefaultLocationModal(asset: any) {
      // @ts-ignore
      this.$refs.defaultLocationModalRef?.openModal(asset)
    },
    handleAssetLocationSaved() {
      // Reload the grid after saving
      if (this.grid) {
        this.grid.refresh()
      }
    },
    openAssetImagesModal(asset: any) {
      // @ts-ignore
      this.$refs.assetImagesModalRef?.openModal(asset)
    },
    handleAssetImagesSaved() {
      if (this.grid) {
        this.grid.refresh()
      }
    }
  }
})
</script>

<style src="@/assets/scss/asset-view-style.scss" lang="scss"></style>
